import { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cx } from "@/lib/cx.js";
import { toneOf } from "@/lib/tokens.js";
import { CONFIRM_TONES, FeedbackContext, TOAST_TONES } from "./feedback.js";
import Button from "./Button.jsx";
import { useOverlayBehaviour } from "@/hooks/useOverlayBehaviour.js";

/*
 * Hosts the toast stack and the single confirm dialog. Mount once, near the root.
 * See feedback.js for the hooks and the reasoning.
 */
export default function FeedbackProvider({ children }) {
    /* ---- Toasts ---------------------------------------------------------- */
    const [toasts, setToasts] = useState([]);
    const seq = useRef(0);

    const toast = useCallback((message, tone = "success", { duration = 2600 } = {}) => {
        const id = ++seq.current;
        setToasts((list) => [...list, { id, message, tone }]);
        window.setTimeout(() => {
            setToasts((list) => list.filter((t) => t.id !== id));
        }, duration);
        return id;
    }, []);

    /* ---- Confirm ---------------------------------------------------------
     * One dialog at a time. `resolver` holds the promise's resolve so the caller
     * can await the user's answer instead of passing a callback in.
     * -------------------------------------------------------------------- */
    const [dialog, setDialog] = useState(null);
    const resolver = useRef(null);

    const confirm = useCallback((options) => {
        return new Promise((resolve) => {
            resolver.current = resolve;
            setDialog(options);
        });
    }, []);

    const settle = useCallback((answer) => {
        setDialog(null);
        const resolve = resolver.current;
        resolver.current = null;
        resolve?.(answer);
    }, []);

    const value = useMemo(() => ({ toast, confirm }), [toast, confirm]);

    return (
        <FeedbackContext.Provider value={value}>
            {children}

            {createPortal(
                <div
                    aria-live="polite"
                    aria-atomic="false"
                    className="pointer-events-none fixed bottom-5 left-1/2 z-80 flex -translate-x-1/2 flex-col items-center gap-2"
                >
                    {toasts.map((t) => (
                        <Toast key={t.id} {...t} />
                    ))}
                </div>,
                document.body,
            )}

            <ConfirmDialog options={dialog} onSettle={settle} />
        </FeedbackContext.Provider>
    );
}

function Toast({ message, tone }) {
    const t = TOAST_TONES[tone] ?? TOAST_TONES.info;

    return (
        <div className="flex items-center gap-2.5 rounded-lg bg-slate-900 px-4 py-2.5 text-[13px] font-medium text-white shadow-xl dark:bg-slate-700">
            <svg
                className={cx("size-4 shrink-0", t.color)}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={t.strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: t.icon }}
            />
            <span>{message}</span>
        </div>
    );
}

function ConfirmDialog({ options, onSettle }) {
    const open = Boolean(options);
    const panelRef = useOverlayBehaviour({ open, onClose: () => onSettle(false) });

    if (!open) return null;

    const {
        tone = "warning",
        title,
        body,
        okLabel = "Confirm",
        cancelLabel = "Cancel",
    } = options;

    const cfg = CONFIRM_TONES[tone] ?? CONFIRM_TONES.warning;
    const t = toneOf(cfg.tone);

    return createPortal(
        <div className="fixed inset-0 z-70">
            <div
                onClick={() => onSettle(false)}
                className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
                aria-hidden="true"
            />

            <div className="relative flex h-full items-center justify-center p-4">
                <div
                    ref={panelRef}
                    role="alertdialog"
                    aria-modal="true"
                    aria-label={typeof title === "string" ? title : "Confirm"}
                    tabIndex={-1}
                    className="w-full max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl outline-none dark:border-slate-700 dark:bg-slate-900"
                >
                    <div className="flex gap-3.5 p-5">
                        <span
                            className={cx("grid size-10 shrink-0 place-items-center rounded-full", t.icon)}
                        >
                            <ConfirmIcon tone={tone} />
                        </span>

                        <div className="min-w-0">
                            <h3 className="text-[15px] font-semibold">{title}</h3>
                            <div className="mt-1.5 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
                                {body}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2.5 border-t border-slate-200 px-5 py-3 dark:border-slate-800">
                        <Button onClick={() => onSettle(false)}>{cancelLabel}</Button>
                        {/* Autofocus the confirm button so Enter completes the
                            action a keyboard user just triggered. Destructive
                            dialogs still read their consequences above it. */}
                        <Button
                            variant={cfg.okVariant}
                            className="ml-auto"
                            data-autofocus
                            onClick={() => onSettle(true)}
                        >
                            {okLabel}
                        </Button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}

function ConfirmIcon({ tone }) {
    const paths = {
        danger: <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />,
        warning: (
            <>
                <path d="M10.3 4 2.6 17.5A1.6 1.6 0 0 0 4 20h16a1.6 1.6 0 0 0 1.4-2.5L13.7 4a1.6 1.6 0 0 0-2.8 0Z" />
                <path d="M12 9.5v4M12 17h.01" />
            </>
        ),
        success: <path d="M20 6 9 17l-5-5" />,
        info: (
            <>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 16v-4M12 8h.01" />
            </>
        ),
    };

    return (
        <svg
            className="size-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {paths[tone] ?? paths.warning}
        </svg>
    );
}
